import { ChangeDetectionStrategy, Component, computed, effect, inject, isDevMode, Signal } from '@angular/core';
import { OnlineService } from '../../online.service';
import { KeyValue, KeyValuePipe } from '@angular/common';
import { DataService } from './data.service';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  asyncScheduler,
  buffer,
  connect,
  ignoreElements,
  merge,
  Observable,
  pipe,
  scan,
  share,
  throttle,
  timer,
  UnaryFunction,
} from 'rxjs';
import { GameServer } from '../../model/gameserver';
import { TsViewerComponent } from './ts-viewer/ts-viewer.component';
import { ServerComponent } from './server/server.component';
import { MasonryOptions } from '../../masonry/masonry.models';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { AppMasonryComponent } from '../../masonry/app-masonry.component';
import { AppMasonryItemDirective } from '../../masonry/app-masonry-item.directive';
import { SocketService } from './socket.service';
import { ConnectionStatusComponent } from './connection-status/connection-status.component';

@Component({
  selector: 'app-serverlist',
  imports: [
    TsViewerComponent,
    ServerComponent,
    AppMasonryComponent,
    AppMasonryItemDirective,
    KeyValuePipe,
    ConnectionStatusComponent,
  ],
  templateUrl: './serverlist.component.html',
  styleUrl: './serverlist.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServerlistComponent {
  protected readonly dataService = inject(DataService);
  private readonly onlineService = inject(OnlineService);

  private readonly socketService = inject(SocketService);

  public masonryOptions: MasonryOptions = {
    columnWidth: '.grid-sizer',
    horizontalOrder: false,
    percentPosition: true,
    animations: {
      enterClass: 'slide-fade-in',
      leaveClass: 'slide-fade-out',
      settledClass: 'masonry-settled',
    },
  };

  readonly servers = toSignal(
    this.dataService.serverUpdates$.pipe(
      throttledBuffer(25, 2000),
      scan((acc, values) => {
        values.forEach(value => {
          acc.set(value.id, value);
        });
        return new Map(acc);
      }, new Map<string, GameServer>()),
      distinctUntilChanged(),
      startWith(new Map<string, GameServer>())
    ),
    { requireSync: true }
  );

  readonly e = isDevMode() && [
    effect(() => console.log('Serverlist: Servers', this.servers())),
    effect(() => console.log('Serverlist: Online', this.online())),
  ];

  readonly online = toSignal(this.onlineService.status$, { requireSync: true });
  readonly socketOnline = toSignal(this.socketService.onlineStatus$);

  readonly status: Signal<'unknown' | 'offline' | 'pull' | 'push'> = computed(() => {
    const online = this.online();
    if (online === 'offline') {
      return 'offline';
    } else if (online === 'online') {
      if (this.socketOnline() === true) {
        return 'push';
      }
      return 'pull';
    }
    return 'unknown';
  });

  sortServer<K extends KeyValue<string, GameServer>>(a: K, b: K) {
    const criteria = ['online', 'type', 'name', 'id'] as const;
    const orders = ['desc', 'asc', 'asc', 'asc'] as const;

    for (let i = 0; i < criteria.length; i++) {
      const key = criteria[i];
      const order = orders[i];
      const aValue = a.value[key];
      const bValue = b.value[key];

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
    }

    return 0; // They are equal in all criteria
  }
}

// from https://stackoverflow.com/a/66941053/3757624
function throttledBuffer<T>(
  initialTimeMs: number,
  throttleTimeMs: number
): UnaryFunction<Observable<T>, Observable<T[]>> {
  return pipe(
    connect(observable$ => {
      const throttleCalls$ = observable$.pipe(
        throttle(() => timer(initialTimeMs, throttleTimeMs, asyncScheduler), { leading: false, trailing: true })
      );

      const src$ = observable$.pipe(share());
      return merge(src$.pipe(ignoreElements()), src$.pipe(buffer(throttleCalls$)));
    })
  );
}
